'use client';
import SummaryLine from '@components/Cart/SummaryLine';
import { Markdown } from '@repo/ui/src/components/Markdown';
import useBasket from '@hooks/basket/useBasket';
import { ImageProps } from '@repo/ui/src/types/image';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import React from 'react';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import Image from '@repo/ui/src/components/Image';

type OrderSummaryProps = {
  isCartPage?: boolean;
};

const OrderSummary = ({ isCartPage = false }: OrderSummaryProps) => {
  const { data: { shippingTotal = 0, subtotal = 0, totalItems = 0, tax = 0 } = {} } = useBasket();
  const { t } = useTranslation();
  const cartPromotionalMarkdown = t('cart.cartPromotionalMarkdown', undefined, { default: 'none' });
  const cartPromotionalIcon = t<ImageProps>('cart.cartPromotionalIcon', undefined, {
    returnObjects: true,
    default: undefined,
  });
  const imageUrl = buildImageSrc(cartPromotionalIcon?.image, 200, 0, 0);

  return (
    <div className="tw-grid tw-gap-y-2">
      <SummaryLine
        key="subtotal"
        bold={isCartPage}
        title={t(
          'orderSummary.subtotalItems',
          {
            totalItems,
          },
          { default: `Subtotal ({{totalItems}} items)` },
        )}
        value={subtotal}
      />
      <SummaryLine
        key="shipping"
        title={t('cart.shipping', undefined, {
          default: 'Shipping',
        })}
        value={shippingTotal}
        freeLabel={t('addToCartWindowContent.shippingFreeLabel', undefined, {
          default: 'Free',
        })}
      />
      <SummaryLine
        key="tax"
        title={t('cart.estimatedTax', undefined, {
          default: 'Estimated Tax',
        })}
        value={tax}
        freeLabel={t('addToCartWindowContent.taxFreeLabel', undefined, {
          default: '-',
        })}
      />

      {cartPromotionalMarkdown !== 'none' && (
        <div className="tw-bg-warm-200 tw-p-6 tw-mt-3 tw-flex tw-gap-4">
          <div className="tw-w-20">
            <Image src={imageUrl} height={30} width={30} alt={cartPromotionalIcon?.altTag} />
          </div>
          <Markdown>{cartPromotionalMarkdown}</Markdown>
        </div>
      )}
    </div>
  );
};

export default OrderSummary;
